import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLock, faGlobe, faChartLine, faHeart, faTrophy, faCog } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/GlobalStyles';

const HeroSection = styled.section`
  padding: 120px 0 100px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 100px 0 80px;
  }
  
  @media (max-width: 480px) {
    padding: 90px 0 70px;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 30px;
`;

const LogoImage = styled.img`
  width: 150px;
  height: 150px;
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.8rem;
  margin-bottom: 20px;
  background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 15px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: ${theme.colors.textSecondary};
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 20px;
    line-height: 1.6;
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0 15px;
    line-height: 1.5;
    margin-bottom: 25px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    margin-top: 30px;
  }
`;

const PrimaryButton = styled.a`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.secondary};
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background-color: ${theme.colors.accent};
    color: ${theme.colors.secondary};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(255, 107, 107, 0.3);
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 576px) {
    width: 85%;
    max-width: 300px;
    justify-content: center;
    padding: 12px 24px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    width: 90%;
    padding: 10px 20px;
    font-size: 0.95rem;
  }
`;

const SecondaryButton = styled(Link)`
  background-color: transparent;
  color: ${theme.colors.secondary};
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  border: 2px solid ${theme.colors.primary};
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background-color: ${theme.colors.primary}20;
    color: ${theme.colors.primary};
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 576px) {
    width: 85%;
    max-width: 300px;
    justify-content: center;
    padding: 12px 24px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    width: 90%;
    padding: 10px 20px;
    font-size: 0.95rem;
  }
`;

const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: ${theme.colors.backgroundAlt};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, ${theme.colors.background}, ${theme.colors.backgroundAlt});
  }
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.8rem;
  margin-bottom: 80px;
  position: relative;
  
  span {
    color: ${theme.colors.primary};
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 60px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 50px;
    
    &:after {
      width: 60px;
      height: 3px;
    }
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    gap: 30px;
  }
  
  @media (max-width: 480px) {
    gap: 25px;
    padding: 0 15px;
  }
`;

const FeatureCard = styled.div`
  background-color: rgba(46, 46, 46, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  transition: all 0.4s ease;
  position: relative;
  z-index: 1;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 30px 25px;
  }
  
  @media (max-width: 480px) {
    padding: 25px 20px;
    border-radius: 12px;
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 25px;
  transition: all 0.3s ease;
  
  ${FeatureCard}:hover & {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  transition: color 0.3s ease;
  
  ${FeatureCard}:hover & {
    color: ${theme.colors.primary};
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.7;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const CTASection = styled.section`
  padding: 120px 0;
  text-align: center;
  background: linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.backgroundAlt} 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  color: ${theme.colors.textSecondary};
  max-width: 700px;
  margin: 0 auto 50px;
  line-height: 1.8;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 20px;
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0 15px;
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

const Home = () => {
  return (
    <>
      <HeroSection>
        <div className="container">
          <HeroTitle>
            TuSecreto Bot
          </HeroTitle>
          <HeroSubtitle>
            ¡Buen día! Tras nuestro arduo desarrollo de la aplicación web, queremos presentarte nuestro bot de Discord para compartir secretos anónimos en tu servidor.
          </HeroSubtitle>
          <ButtonGroup>
            <PrimaryButton 
              href="https://discord.com/oauth2/authorize?client_id=1380674741685321758&permissions=8&redirect_uri=https%3A%2F%2Flpxmpqpmoofsrlbenatl.supabase.co%2Fauth%2Fv1%2Fcallback&integration_type=0&scope=bot" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Invitar Bot <FontAwesomeIcon icon={faArrowRight} />
            </PrimaryButton>
            <SecondaryButton to="/comandos">
              Ver Comandos
            </SecondaryButton>
          </ButtonGroup>
        </div>
      </HeroSection>

      <FeaturesSection>
        <div className="container">
          <SectionTitle>Funcionalidades <span>Principales</span></SectionTitle>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faLock} />
              </FeatureIcon>
              <FeatureTitle>Secretos Anónimos</FeatureTitle>
              <FeatureDescription>
                Comparte tus secretos de forma completamente anónima en tu servidor de Discord. Nadie sabrá quién los envió.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faGlobe} />
              </FeatureIcon>
              <FeatureTitle>Secretos Globales</FeatureTitle>
              <FeatureDescription>
                Activa los secretos globales para compartir entre todos los servidores que usan el bot. ¡Más diversión y conexiones!
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faChartLine} />
              </FeatureIcon>
              <FeatureTitle>Sistema de Niveles</FeatureTitle>
              <FeatureDescription>
                Sube de nivel a medida que compartes más secretos. Compite con otros usuarios por el ranking más alto.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faHeart} />
              </FeatureIcon>
              <FeatureTitle>Reacciones a Secretos</FeatureTitle>
              <FeatureDescription>
                Reacciona a los secretos con emojis como ❤️, 😂, 😮, 😢 y 😡 de forma anónima.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faTrophy} />
              </FeatureIcon>
              <FeatureTitle>Ranking de Popularidad</FeatureTitle>
              <FeatureDescription>
                Descubre los secretos más populares con el sistema de ranking basado en reacciones.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <FontAwesomeIcon icon={faCog} />
              </FeatureIcon>
              <FeatureTitle>Configuración Sencilla</FeatureTitle>
              <FeatureDescription>
                Configura fácilmente el canal de secretos y otras opciones con comandos simples.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </div>
      </FeaturesSection>

      <CTASection>
        <div className="container">
          <CTATitle>¿Listo para compartir tus secretos?</CTATitle>
          <CTADescription>
            Únete a nuestra comunidad y comienza a compartir tus secretos de forma anónima hoy mismo.
          </CTADescription>
          <ButtonGroup>
            <PrimaryButton 
              href="https://discord.com/oauth2/authorize?client_id=1380674741685321758&permissions=8&redirect_uri=https%3A%2F%2Flpxmpqpmoofsrlbenatl.supabase.co%2Fauth%2Fv1%2Fcallback&integration_type=0&scope=bot" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Añadir a mi Servidor <FontAwesomeIcon icon={faArrowRight} />
            </PrimaryButton>
            <SecondaryButton 
              to="/guia"
            >
              Leer la Guía
            </SecondaryButton>
          </ButtonGroup>
        </div>
      </CTASection>
    </>
  );
};

export default Home; 